import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowRight, Check, BookOpen, Video, Users, Award, Music, Sparkles, Target, Clock, Download, MessageCircle, Quote, Star, ChevronDown, ChevronUp } from "lucide-react";
import { Link } from "react-router-dom";
import { useState } from "react";
import heroImage from "@/assets/hero-paedagogische-fachkraefte.jpg";
import notesWatercolor from "@/assets/notes-watercolor.png";
import drumWatercolor from "@/assets/drum-watercolor.png";
import glockspielWatercolor from "@/assets/glockenspiel-watercolor.png";
import ukuleleWatercolor from "@/assets/ukulele-watercolor.png";
import laptopWatercolor from "@/assets/laptop-watercolor.png";
import rasselnWatercolor from "@/assets/rasseln-watercolor.png";
import monikaRoebke from "@/assets/testimonials/monika-roebke.jpg";
import brittaWiegand from "@/assets/testimonials/britta-wiegand.jpg";
import christianeBeckert from "@/assets/testimonials/christiane-beckert.jpg";
import SEO from "@/components/SEO";
import { FACHKRAEFTE_KURZ, KURSE_TEXT, gross } from "@/config/webseiteFakten";

const PaedagogischeFachkraefte = () => {
  const [expandedCards, setExpandedCards] = useState<number[]>([]);
  
  const toggleExpanded = (index: number) => {
    setExpandedCards(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength) + "...";
  };

  const testimonials = [
    {
      name: "Monika Röbke",
      role: "Erzieherin",
      text: "Bei Musikonzept finde ich zu jedem Thema unkompliziert jede Menge Ideen die ich gleich im Alltag nutzen kann.",
      image: monikaRoebke
    },
    {
      name: "Britta Wiegand",
      role: "Erzieherin",
      text: "Musikonzept begleitet mich nun schon mehrere Jahre und ich entdecke immer wieder Neues. Ukulele, Gitarre, Praxis und Theorie, die Plattform ermöglicht mir ein Lernen in meinem Tempo.",
      image: brittaWiegand
    },
    {
      name: "Christiane Beckert",
      role: "Erzieherin",
      text: "Durch MusiKonzept konnte ich mein Gitarrenspiel verbessern und habe viele neue inspirierende Stücke kennengelernt. Die Webinare sind sehr gut und geben viele neue Impulse. Es erweitert meinen Alltag in der Krippe sehr. Danke für die Ausbildung und alles was noch kommt.",
      image: christianeBeckert
    }
  ];

  const benefits = [
    {
      icon: BookOpen,
      title: "Praxisnahe Fortbildungen",
      description: `${gross(KURSE_TEXT)} mit direktem Bezug zu deinem Kita- oder Schulalltag`
    },
    {
      icon: Video,
      title: "Flexible Online-Kurse",
      description: "Lerne wann und wo du willst – berufsbegleitend und in deinem Tempo"
    },
    {
      icon: Award,
      title: "Zertifizierte Abschlüsse",
      description: "Ausbildungen mit Zertifikat; Fachkraft für Musik ist ein staatlich zugelassener Fernlehrgang (ZFU)"
    },
    {
      icon: Users,
      title: "Lebendige Community",
      description: "Austausch mit Fachkräften und Expert:innen"
    },
    {
      icon: Music,
      title: "Sofort umsetzbar",
      description: "Fertige Konzepte, Lieder und Materialien für deine tägliche Arbeit"
    },
    {
      icon: Download,
      title: "Umfangreiche Materialien",
      description: "PDFs, Noten, Playbacks und Vorlagen zum Download"
    }
  ];


  const features = [
    {
      icon: Target,
      title: "Fachkraft für Musik im Elementarbereich",
      description: "Umfassende Online-Ausbildung mit 330 Stunden, staatlich zugelassener Fernlehrgang (ZFU)",
      link: "/ausbildung/fachkraft-musik",
      external: true,
      color: "from-primary/10 to-primary/5"
    },
    {
      icon: Music,
      title: "Der große Kita-Gitarrenkurs",
      description: "Lerne Kinderlieder zu begleiten – auch ohne Vorkenntnisse",
      link: "/ausbildung/fachkraft-gitarre",
      external: true,
      color: "from-accent/10 to-accent/5"
    },
    {
      icon: Sparkles,
      title: "Einzelne Webinare & Module",
      description: "Flexible Weiterbildung zu spezifischen Themen",
      link: "/kurse",
      external: false,
      color: "from-secondary/10 to-muted/5"
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO title={`Für pädagogische Fachkräfte – Fortbildung & Praxis | MusiKonzept`} description={`Online-Fortbildungen, Praxiskurse und Ausbildungen für ErzieherInnen, Lehrkräfte und pädagogische Fachkräfte.`} path="/paedagogische-fachkraefte" />
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-40 pb-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-accent/5" />
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, hsl(var(--primary)) 1px, transparent 0)',
            backgroundSize: '32px 32px'
          }} />
        </div>
        
        <div className="container relative mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-block">
                <span className="px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium">
                  Für pädagogische Fachkräfte
                </span>
              </div>
              
              <h1 className="text-5xl lg:text-6xl font-bold text-foreground leading-tight">
                Endlich Ideen für musikalische Angebote, die{" "}
                <span className="text-primary">funktionieren</span>
              </h1>
              
              <p className="text-xl text-muted-foreground leading-relaxed">
                Du möchtest mehr Musik in deine Einrichtung bringen, aber dir fehlen oft die Ideen und das Know-how? 
                Wir helfen dir dabei – mit praxisnahen Fortbildungen, die direkt umsetzbar sind.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <Button asChild size="lg" className="gap-2">
                  <a href="/kurse">
                    Entdecke unsere Fortbildungen
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </Button>
                <Button asChild variant="outline" size="lg">
                  <a href="/termine?kategorie=Kostenlose+Webinare" target="_blank" rel="noopener noreferrer">
                    Kostenlose Webinare
                  </a>
                </Button>
              </div>
              
              <div className="flex items-center gap-6 pt-4">
                <div className="flex -space-x-2">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-accent border-2 border-background" />
                  ))}
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">{FACHKRAEFTE_KURZ} Fachkräfte</p>
                  <p className="text-xs text-muted-foreground">vertrauen auf MusiKonzept</p>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-accent/20 rounded-3xl blur-3xl" />
              <img 
                src={heroImage} 
                alt="Musikalische Früherziehung" 
                className="relative rounded-3xl shadow-2xl w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Pain Points Section */}
      <section className="relative py-20 bg-gradient-to-br from-accent/10 via-primary/5 to-background overflow-hidden">
        {/* Watercolor decorations */}
        <div className="absolute top-10 right-10 w-64 h-64 opacity-10 pointer-events-none animate-pulse">
          <img src={notesWatercolor} alt="" className="w-full h-full object-contain" />
        </div>
        <div className="absolute bottom-10 left-10 w-48 h-48 opacity-10 pointer-events-none animate-pulse" style={{ animationDelay: '1s' }}>
          <img src={drumWatercolor} alt="" className="w-full h-full object-contain" />
        </div>
        
        <div className="container relative mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-foreground mb-4">
              Kennst du das?
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Typische Herausforderungen im pädagogischen Alltag – und wie MusiKonzept dir hilft
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {[
              {
                problem: "Mir fehlen Ideen für musikalische Angebote",
                solution: "Über 100 fertige Module mit Liedern, Spielen und Materialien zum sofortigen Einsatz"
              },
              {
                problem: "Ich bin keine Musikerin und fühle mich unsicher",
                solution: "Schritt-für-Schritt Anleitungen – auch ohne musikalische Vorkenntnisse umsetzbar"
              },
              {
                problem: "Ich habe wenig Zeit für lange Fortbildungen",
                solution: "Flexible Online-Kurse, die du berufsbegleitend in deinem Tempo absolvieren kannst"
              },
              {
                problem: "Ich weiß nicht, wo ich anfangen soll",
                solution: "Strukturierte Ausbildungen mit klarem Lernpfad und persönlicher Begleitung"
              }
            ].map((item, index) => (
              <Card key={index} className="p-6 bg-gradient-to-br from-background to-muted/30 hover:shadow-2xl hover:shadow-primary/10 transition-all duration-300 hover:-translate-y-2 hover:scale-[1.02] border-border/50 group">
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-full bg-gradient-to-br from-accent/20 to-primary/10 mt-1 group-hover:scale-110 transition-transform">
                      <MessageCircle className="w-5 h-5 text-accent" />
                    </div>
                    <p className="text-foreground font-medium italic">
                      "{item.problem}"
                    </p>
                  </div>
                  <div className="flex items-start gap-3 pl-11">
                    <div className="flex-shrink-0">
                      <div className="w-1 h-8 bg-gradient-to-b from-primary to-accent rounded-full" />
                    </div>
                    <div className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                      <p className="text-muted-foreground leading-relaxed">
                        {item.solution}
                      </p>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="relative py-20 bg-gradient-to-b from-background via-muted/10 to-primary/5 overflow-hidden">
        {/* Watercolor decorations */}
        <div className="absolute top-20 left-5 w-56 h-56 opacity-10 pointer-events-none animate-pulse">
          <img src={glockspielWatercolor} alt="" className="w-full h-full object-contain" />
        </div>
        <div className="absolute bottom-20 right-5 w-52 h-52 opacity-10 pointer-events-none animate-pulse" style={{ animationDelay: '1.5s' }}>
          <img src={ukuleleWatercolor} alt="" className="w-full h-full object-contain" />
        </div>
        
        <div className="container relative mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-foreground mb-4">
              Warum MusiKonzept?
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Wir verstehen deinen Alltag – und bieten dir genau die Unterstützung, die du brauchst.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <Card key={index} className="p-6 bg-gradient-to-br from-background via-background to-muted/20 hover:shadow-2xl hover:shadow-accent/10 transition-all duration-300 hover:-translate-y-2 hover:scale-[1.02] border-border/50 group">
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-xl bg-gradient-to-br from-primary/15 to-accent/10 text-primary group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                    <benefit.icon className="w-6 h-6" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-foreground mb-2">
                      {benefit.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {benefit.description}
                    </p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="relative py-20 bg-gradient-to-br from-accent/5 via-background to-primary/5 overflow-hidden">
        {/* Watercolor decorations */}
        <div className="absolute top-10 left-10 w-64 h-64 opacity-10 pointer-events-none animate-pulse">
          <img src={notesWatercolor} alt="" className="w-full h-full object-contain" />
        </div>
        <div className="absolute bottom-10 right-10 w-52 h-52 opacity-10 pointer-events-none animate-pulse" style={{ animationDelay: '0.5s' }}>
          <img src={ukuleleWatercolor} alt="" className="w-full h-full object-contain" />
        </div>
        
        <div className="container relative mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="px-4 py-2 rounded-full bg-accent/10 text-accent text-sm font-medium">
                Erfahrungen
              </span>
            </div>
            <h2 className="text-4xl font-bold text-foreground mb-4">
              Das sagen Erzieher:innen über MusiKonzept
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Echte Erfahrungen von pädagogischen Fachkräften, die ihre musikalische Arbeit transformiert haben
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => {
              const maxLength = 200;
              const isLongText = testimonial.text.length > maxLength;
              const isExpanded = expandedCards.includes(index);
              const displayText = isLongText && !isExpanded 
                ? truncateText(testimonial.text, maxLength)
                : testimonial.text;

              return (
                <Card 
                  key={index} 
                  className="relative p-8 bg-gradient-to-br from-background via-muted/20 to-background border-border/50 hover:shadow-2xl hover:shadow-accent/10 transition-all duration-500 hover:-translate-y-3 hover:scale-[1.02] group overflow-hidden flex flex-col"
                  style={{ 
                    animationDelay: `${index * 150}ms`,
                    animation: 'fade-in 0.6s ease-out forwards'
                  }}
                >
                  {/* Quote decoration */}
                  <div className="absolute top-4 right-4 opacity-10 group-hover:opacity-20 transition-opacity">
                    <Quote className="w-16 h-16 text-primary" />
                  </div>
                  
                  {/* Content */}
                  <div className="relative space-y-6 flex flex-col flex-1">
                    {/* Profile Image */}
                    <div className="relative w-20 h-20 mx-auto">
                      <img 
                        src={testimonial.image} 
                        alt={testimonial.name}
                        className="w-full h-full rounded-full object-cover ring-4 ring-primary/20 group-hover:ring-primary/40 transition-all duration-300"
                      />
                      <div className="absolute -bottom-2 -right-2 w-10 h-10 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                        <Quote className="w-5 h-5 text-primary-foreground" />
                      </div>
                    </div>
                    
                    {/* Rating Stars */}
                    <div className="flex gap-1 justify-center">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className="w-5 h-5 fill-accent text-accent"
                        />
                      ))}
                    </div>
                    
                    {/* Testimonial Text */}
                    <div className="flex-1">
                      <p className="text-foreground leading-relaxed text-center italic">
                        "{displayText}"
                      </p>
                      
                      {isLongText && (
                        <button
                          onClick={() => toggleExpanded(index)}
                          className="mt-2 text-sm text-primary hover:text-primary/80 font-medium flex items-center gap-1 transition-colors mx-auto"
                        >
                          {isExpanded ? (
                            <>
                              Weniger lesen
                              <ChevronUp className="w-4 h-4" />
                            </>
                          ) : (
                            <>
                              Mehr lesen
                              <ChevronDown className="w-4 h-4" />
                            </>
                          )}
                        </button>
                      )}
                    </div>
                    
                    {/* Author */}
                    <div className="pt-6 border-t border-border/50 text-center">
                      <p className="font-semibold text-foreground">{testimonial.name}</p>
                      <p className="text-sm text-muted-foreground">{testimonial.role}</p>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
          
          {/* Trust Badge */}
          <div className="mt-12 text-center">
            <p className="text-sm text-muted-foreground">
              Über <span className="font-bold text-primary">10.000 Fachkräfte</span> nutzen MusiKonzept für ihre tägliche Arbeit
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="angebote" className="relative py-20 bg-gradient-to-br from-primary/5 via-background to-accent/10 overflow-hidden">
        {/* Watercolor decorations */}
        <div className="absolute top-10 right-20 w-60 h-60 opacity-10 pointer-events-none animate-pulse">
          <img src={laptopWatercolor} alt="" className="w-full h-full object-contain" />
        </div>
        <div className="absolute bottom-10 left-20 w-56 h-56 opacity-10 pointer-events-none animate-pulse" style={{ animationDelay: '1.2s' }}>
          <img src={rasselnWatercolor} alt="" className="w-full h-full object-contain" />
        </div>
        
        <div className="container relative mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-foreground mb-4">
              Unsere Angebote für dich
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Von einzelnen Webinaren bis zur vollständigen Ausbildung – wähle, was zu dir passt.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className={`relative p-8 bg-gradient-to-br ${feature.color} border-border/50 hover:shadow-2xl hover:shadow-primary/20 transition-all duration-500 hover:-translate-y-3 hover:scale-[1.03] group overflow-hidden`}>
                {/* Animated gradient overlay on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-primary/0 via-accent/0 to-primary/0 group-hover:from-primary/5 group-hover:via-accent/5 group-hover:to-primary/5 transition-all duration-500" />
                
                <div className="relative space-y-4">
                  <div className="p-4 rounded-2xl bg-background/80 w-fit group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 shadow-lg">
                    <feature.icon className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold text-foreground">
                    {feature.title}
                  </h3>
                  <p className="text-muted-foreground">
                    {feature.description}
                  </p>
                  <Button asChild variant="ghost" className="gap-2 group/btn">
                    {feature.external ? (
                      <a href={feature.link} target="_blank" rel="noopener noreferrer">
                        Mehr erfahren
                        <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                      </a>
                    ) : (
                      <Link to={feature.link}>
                        Mehr erfahren
                        <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                      </Link>
                    )}
                  </Button>
                </div>
              </Card>
            ))}
          </div>
          
          {/* Membership Hint */}
          <div className="mt-12 text-center">
            <Card className="inline-block p-6 bg-gradient-to-r from-accent/5 to-primary/5">
              <p className="text-muted-foreground">
                <span className="font-semibold text-foreground">Tipp:</span> Als Mitglied erhältst du Zugang zu allen Inhalten und sparst bei Ausbildungen.{" "}
                <Link to="/preise" className="text-primary hover:underline font-medium">
                  Mehr erfahren →
                </Link>
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <Card className="relative overflow-hidden bg-gradient-to-br from-primary via-primary/90 to-accent">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0" style={{
                backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
                backgroundSize: '32px 32px'
              }} />
            </div>
            
            <div className="relative p-12 text-center">
              <div className="max-w-2xl mx-auto space-y-6">
                <h2 className="text-3xl md:text-4xl font-bold text-primary-foreground">
                  Finde die passende Fortbildung für dich
                </h2>
                <p className="text-lg text-primary-foreground/90">
                  Starte noch heute und bringe mehr Musik in deinen pädagogischen Alltag – wir begleiten dich dabei.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                  <Button asChild size="lg" variant="secondary" className="gap-2">
                    <a href="/ausbildung/fachkraft-musik" target="_blank" rel="noopener noreferrer">
                      Ausbildungen ansehen
                      <ArrowRight className="w-4 h-4" />
                    </a>
                  </Button>
                  <Button asChild size="lg" variant="outline" className="bg-transparent border-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/10">
                    <a href="/termine?kategorie=Kostenlose+Webinare" target="_blank" rel="noopener noreferrer">
                      Kostenlose Webinare
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PaedagogischeFachkraefte;
